import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import * as Highcharts from 'highcharts';
import HeatmapModule from 'highcharts/modules/heatmap';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import api from '../../../../api';
import { createHeatmapDatas, ProcessedHeatmapData } from "@/pages/residents/detail/benTrendAnalysis/createHeatmapDatas";
import { getDayStartToEndRange } from '@/utils/dateutil';

HeatmapModule(Highcharts);

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export type BenGraphFetchFailedReason = 'TOO_LONG_PERIOD' | 'NO_DATA' | 'UNKNOWN_ERROR';

type BenGraphFetchResult =
| { success: true; data: BenGraphData }
| { success: false; reason: BenGraphFetchFailedReason };

interface Props {
  startDate: Dayjs;
  endDate: Dayjs;
  residentId: string;
  timeRange: string;
  selectedStartTime: string;
  selectedEndTime: string;
}

interface HeatmapGraph {
  data: number[][];
  xCategories: string[];
  yCategories: string[];
}

export interface allHeatmapGraphData {
  main: HeatmapGraph;
  summary: HeatmapGraph;
}

interface ScatterItem {
  x: number;
  y: number;
  color: string;
  name: string;
}

interface BenGraphData {
  sensor: allHeatmapGraphData;
  temperature: allHeatmapGraphData;
  humidity: allHeatmapGraphData;
  activity: HeatmapGraph;
  careRecordPlot: ScatterItem[];
  notificationPlot: ScatterItem[];
  activityPositionChangedPlot: {x: number, y: number}[];
  highRiskTimes: Dayjs[][];
  hasHeatmapData: boolean;
}

export const useFetchBenGraphData = () => {

  // データ取得閾値 期間内にこれ以上のデータセットのcount総数がある場合はfetchを中止する
  const dataCountThreshold = 3000000; // about 104 days (sampling rate: 3sec => 20datas/min => 3000000 / (20 * 60 * 60) ≒ 104.2 days)

  const [benGraphData, setBenGraphData] = React.useState<BenGraphData>();

  const fetchBenGraphData = async (
      {startDate, endDate, residentId, timeRange, selectedStartTime, selectedEndTime,}: Props
  ): Promise<BenGraphFetchResult> => {

    const getTimeRange = (time: Dayjs) => {
      switch (timeRange) {
        case 'all-time': {
          // 今日の0時から翌日0時までの範囲を返す
          return getDayStartToEndRange(time, 0, 0);
        }
        case 'day-time': {
          // 今日の7時から19時までの範囲を返す
          const startHour = 7;
          const endHour = 19;
          return getDayStartToEndRange(time, startHour, endHour);
        }

        case 'night-time': {
          // 今日の19時から明日の7時までの範囲を返す
          const startHour = 19;
          const endHour = 7;
          return getDayStartToEndRange(time, startHour, endHour);
        }

        case 'selected-time': {
          // 自由設定時間を返す
          const startHour = +selectedStartTime.split(':')[0];
          const endHour = +selectedEndTime.split(':')[0];
          return getDayStartToEndRange(time, startHour, endHour);
        }
        default:
          throw new Error('Invalid selected timeRange');
      }
    };

    const preCheckDataCount = async (from: string, to: string): Promise<boolean> => {
      const dataset = await api.get('/b-devices/residents/:residentId/gas/data-sets', {
        params: { residentId, from, to, limit: 1000 },
      });

      const total = dataset.reduce((sum, item) => sum + item.number, 0);
      return total > dataCountThreshold;
    }

    const getBenGraphSourceDatas = async (from: string, to: string): Promise<BenGraphFetchResult> => {
      const params = { residentId, from, to, sort: 'asc', limit: dataCountThreshold };

      const [gasTrend, notifications, careRecords] = await Promise.all([
        api.get('/b-devices/gas/residents/:residentId/gas-trend-analysis', { params }),
        api.get('/residents/:residentId/notifications', { params }),
        api.get('/residents/:residentId/care-records', { params }),
      ])

      if (!gasTrend.length && !careRecords.length) {
        return { success: false, reason: 'NO_DATA' };
      }

      const heatmapDatas: ProcessedHeatmapData = createHeatmapDatas(
          gasTrend, careRecords, notifications, timeRange, selectedStartTime, selectedEndTime
      )

      const data = convertDatasToStates(heatmapDatas);

      return { success: true, data };
    }

    const [startTime] = getTimeRange(startDate);
    const [, endTime] = getTimeRange(endDate);

    const from = startTime.subtract(1, 'minute').format('YYYY-MM-DD[T]HH:mm:ss[Z]');
    const to = endTime.add(1, 'minute').format('YYYY-MM-DD[T]HH:mm:ss[Z]');

    try {

      const isOverThreshold = await preCheckDataCount(from, to);

      if (isOverThreshold) {
        return { success: false, reason: 'TOO_LONG_PERIOD' };
      }

      const result = await getBenGraphSourceDatas(from, to);

      if (result.success) {
        setBenGraphData(result.data);
      }

      return result;

    } catch (e) {
      return { success: false, reason: 'UNKNOWN_ERROR' };
    }

  };

  const convertDatasToStates = (heatmapDatas: ProcessedHeatmapData): BenGraphData => {

    const sensorHeatmapData = {
      main: heatmapDatas.sensor.sensorHeatmapData,
      summary: heatmapDatas.sensor.updatedSensorSummaryHeatmap,
    }

    const temperatureHeatMapData = {
      main: heatmapDatas.temperature.temperatureHeatmapData,
      summary: heatmapDatas.temperature.updatedTemperatureSummaryHeatmap,
    }

    const humidityHeatMapData = {
      main: heatmapDatas.humidity.humidityHeatmapData,
      summary: heatmapDatas.humidity.updatedHumiditySummaryHeatmap,
    }

    const activityHeatMapData = heatmapDatas.activity.activityHeatmapData;

    const careRecordPlot = heatmapDatas.careRecordPlot;

    const notificationPlot = heatmapDatas.notificationPlot;

    const activityPositionChangedPlot = heatmapDatas.activity.activityPositionChangedPlot;

    const highRiskTimes = heatmapDatas.highRiskTimes;

    const hasHeatmapData = !!heatmapDatas.sensor.sensorHeatmapData.data.length

    return {
          sensor: sensorHeatmapData,
          temperature: temperatureHeatMapData,
          humidity: humidityHeatMapData,
          activity: activityHeatMapData,
          careRecordPlot: careRecordPlot,
          notificationPlot: notificationPlot,
          activityPositionChangedPlot: activityPositionChangedPlot,
          highRiskTimes: highRiskTimes,
          hasHeatmapData: hasHeatmapData,
        }
  }

  return { benGraphData, fetchBenGraphData };
}
